import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AbstractControl, UntypedFormControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { SharedLanguageService } from '@language';
import { ExtendedBaseProfileField } from '../../../../../core/profiles/entities/extended-profile-field.entity';
import { PartialRecord } from '../../../../../core/records/entities/partial-record.class';
import { EDTFTypeTranslations } from '../../../../../form-controls/components/form-edtf/entities/edtf-type-translations.entity';
import { FormValueRequired, isRequiredControl } from '../../../../../form-controls/helpers/form-value-required';
import { BatchFieldReadHelper } from '../../../../helpers/fields/batch-field-read-helper';
import { FieldReadHelper } from '../../../../helpers/fields/field-read.helper';
import { ShSimpleInputFieldComponent } from '../simple-input-field/simple-input-field.component';

@Component({
    selector: 'sh-edtf-input-field',
    templateUrl: './edtf-input-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShEDTFInputFieldComponent extends ShSimpleInputFieldComponent {
    public typeTranslations: EDTFTypeTranslations;
    constructor(
        protected fieldHelper: FieldReadHelper,
        private readonly language: SharedLanguageService
    ) {
        super(fieldHelper);
        this.typeTranslations = {
            DATE_COMPLETE: this.language.translate('form.edtf.types.date-complete'),
            DATE_PRECISION_YEAR_AND_MONTH: this.language.translate('form.edtf.types.date-precision-year-and-month'),
            DATE_PRECISION_YEAR: this.language.translate('form.edtf.types.date-precision-year'),
            DATETIME_COMPLETE: this.language.translate('form.edtf.types.datetime-complete'),
            DATETIME_COMPLETE_UTC: this.language.translate('form.edtf.types.datetime-complete-utc'),
            DATETIME_COMPLETE_SHIFT: this.language.translate('form.edtf.types.datetime-complete-shift'),
            TIME_INTERVAL: this.language.translate('form.edtf.types.time-interval'),
            LETTER_PREFIXED_YEAR: this.language.translate('form.edtf.types.letter-prefixed-year'),
            SEASONS: this.language.translate('form.edtf.types.seasons'),
            DATE_QUALIFICATION_UNCERTAIN: this.language.translate('form.edtf.types.date-qualification-uncertain'),
            DATE_QUALIFICATION_APPROXIMATE: this.language.translate('form.edtf.types.date-qualification-approximate'),
            DATE_QUALIFICATION_UNCERTAIN_APPROXIMATE: this.language.translate(
                'form.edtf.types.date-qualification-uncertain-approximate'
            ),
            UNSPECIFIED_DIGIT_FROM_THE_RIGHT_DECADE: this.language.translate(
                'form.edtf.types.unspecified-digit-from-the-right-decade'
            ),
            UNSPECIFIED_DIGIT_FROM_THE_RIGHT_CENTURY: this.language.translate(
                'form.edtf.types.unspecified-digit-from-the-right-century'
            ),
            UNSPECIFIED_DIGIT_FROM_THE_RIGHT_MONTH: this.language.translate(
                'form.edtf.types.unspecified-digit-from-the-right-month'
            ),
            UNSPECIFIED_DIGIT_FROM_THE_RIGHT_DAY_IN_YEAR: this.language.translate(
                'form.edtf.types.unspecified-digit-from-the-right-day-in-year'
            ),
            UNSPECIFIED_DIGIT_FROM_THE_RIGHT_DAY_IN_MONTH: this.language.translate(
                'form.edtf.types.unspecified-digit-from-the-right-day-in-month'
            ),
            EXTENDED_INTERVAL_OPEN_END: this.language.translate('form.edtf.types.extended-interval-open-end'),
            EXTENDED_INTERVAL_OPEN_BEGIN: this.language.translate('form.edtf.types.extended-interval-open-begin'),
            EXTENDED_INTERVAL_UNKNOWN_END: this.language.translate('form.edtf.types.extended-interval-unknown-end'),
            EXTENDED_INTERVAL_UNKNOWN_BEGIN: this.language.translate('form.edtf.types.extended-interval-unknown-begin'),
            EXTENDED_INTERVAL: this.language.translate('form.edtf.types.extended-interval'),
            NEGATIVE_CALENDER_YEAR: this.language.translate('form.edtf.types.negative-calendar-year'),
            COMPLETELY_UNSPECIFIED: this.language.translate('form.edtf.types.completely-unspecified')
        };
    }

    init(profileField: ExtendedBaseProfileField, records: PartialRecord[], control: UntypedFormControl): void {
        const value = this.isBatchEdit
            ? BatchFieldReadHelper.getBatchFieldValue(profileField.FieldDefinition)
            : this.fieldHelper.getMergedEDTFFieldValue(profileField.FieldDefinition, records);

        if (isRequiredControl(control)) {
            control.setValidators(
                this.TranslatedFormValueRequired(this.language.translate('form.error.required-field'))
            );
        }
        control.setValue(value);
    }

    private TranslatedFormValueRequired(message: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return FormValueRequired(control, message);
        };
    }
}
